
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close, Edit, Block } from "@mui/icons-material";
import { fetchCashiersByBranch, toggleCashierStatus } from "../../../../../services/Settings/Cashier";
import { fetchAuthRoots } from "../../../../../services/apiService";
import CreateCashier from "./CreateCashier";
import EditCashierForm from "./EditCashierForm";
import DeletedCashiersList from "./DeletedCashiersList";

const CashierListByBranch = () => {
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedBranchName, setSelectedBranchName] = useState("");
  const [cashiers, setCashiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editCashier, setEditCashier] = useState(null);
  const [deletedDialogOpen, setDeletedDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [targetCashier, setTargetCashier] = useState(null);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const data = await fetchAuthRoots();
        setBranches(data);

        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const currentBranchId = user.root_id;

        const currentBranch = data.find((b) => b.id.toString() === currentBranchId?.toString());

        if (currentBranch) {
          setSelectedBranchId(currentBranch.id.toString());
          setSelectedBranchName(currentBranch.name);
          loadCashiers(currentBranch.id.toString());
        } else if (data.length > 0) {
          setSelectedBranchId(data[0].id.toString());
          setSelectedBranchName(data[0].name);
          loadCashiers(data[0].id.toString());
        }
      } catch (err) {
        setError("❌ فشل في تحميل الفروع");
      }
    };
    loadBranches();
  }, []);

  const loadCashiers = async (branchId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCashiersByBranch(branchId);
      const active = data.filter((cashier) => cashier.status === 1);
      setCashiers(active);
    } catch (err) {
      setError("❌ فشل في تحميل الكاشيرات");
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = (e) => {
    const branchId = e.target.value;
    const branch = branches.find((b) => b.id.toString() === branchId);
    setSelectedBranchId(branchId);
    setSelectedBranchName(branch?.name || "");
    localStorage.setItem("root_id", branchId);
    localStorage.setItem("branch_name", branch?.name || "");
    loadCashiers(branchId);
  };

  const handleCashierCreated = () => {
    loadCashiers(selectedBranchId);
    setOpenDialog(false);
  };

  const handleCashierUpdated = () => {
    loadCashiers(selectedBranchId);
    setEditCashier(null);
  };

  const confirmToggleStatus = async () => {
    if (!targetCashier) return;
    try {
      await toggleCashierStatus(targetCashier.id);
      loadCashiers(selectedBranchId);
    } catch (err) {
      alert("❌ فشل في تغيير الحالة.");
    } finally {
      setConfirmDialogOpen(false);
      setTargetCashier(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
    

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>اختر الفرع</InputLabel>
        <Select value={selectedBranchId} onChange={handleBranchChange} label="اختر الفرع">
          {branches.map((branch) => (
            <MenuItem key={branch.id} value={branch.id.toString()}>
              {branch.name} (ID: {branch.id})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button variant="contained" color="primary" onClick={() => setOpenDialog(true)}>
          ➕ إنشاء كاشير جديد
        </Button>
        <Button variant="outlined" color="secondary" onClick={() => setDeletedDialogOpen(true)}>
          🗑️ عرض الكاشيرات المحذوفة
        </Button>
      </Box>

      {loading && <CircularProgress />} 
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <Paper variant="outlined">
          <List>
            {cashiers.length === 0 ? (
              <ListItem>
                <ListItemText primary="لا توجد كاشيرات نشطة لهذا الفرع" />
              </ListItem>
            ) : (
              cashiers.map((cashier) => (
                <ListItem
                  key={cashier.id}
                  divider
                  secondaryAction={
                    <>
                      <Tooltip title="تعديل">
                        <IconButton onClick={() => setEditCashier(cashier)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="إيقاف التفعيل">
                        <IconButton onClick={() => {
                          setTargetCashier(cashier);
                          setConfirmDialogOpen(true);
                        }}>
                          <Block color="warning" />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                >
                  <ListItemText
                    primary={`🧾 ${cashier.name}`}
                    secondary={`🖨️ الطابعة: ${cashier.printer_name} | 📍 الفرع: ${cashier.root_name}`}
                  />
                </ListItem>
              ))
            )}
          </List>
        </Paper>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          إنشاء كاشير جديد
          <IconButton onClick={() => setOpenDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CreateCashier onSuccess={handleCashierCreated} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editCashier} onClose={() => setEditCashier(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          تعديل بيانات الكاشير
          <IconButton onClick={() => setEditCashier(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {editCashier && <EditCashierForm cashier={editCashier} onSuccess={handleCashierUpdated} onClose={() => setEditCashier(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>تأكيد الإجراء</DialogTitle>
        <DialogContent>
          هل أنت متأكد أنك تريد إيقاف تفعيل الكاشير <strong>{targetCashier?.name}</strong>؟
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="secondary">
            إلغاء
          </Button>
          <Button onClick={confirmToggleStatus} color="warning" variant="contained">
            تأكيد
          </Button>
        </DialogActions>
      </Dialog>

      <DeletedCashiersList
  open={deletedDialogOpen}
  onClose={() => setDeletedDialogOpen(false)}
  branchId={selectedBranchId}
  onRestored={() => loadCashiers(selectedBranchId)} // ✅
/>

    </Box>
  );
};

export default CashierListByBranch;
